/**
 * ExpandableTextarea class provides functionality to dynamically resize a textarea element.
 * It listens to input events and resizes the textarea accordingly.
 */
export class ExpandableTextarea {
    /**
     * Constructor for ExpandableTextarea class.
     * @param {Object} $signals - Signal service for event handling.
     * @param {Object} $timeout - Timeout service for delayed actions.
     * @param {Object} $bridges - Bridge service for communication.
     */
    constructor($signals, $timeout, $bridges) {
        this.$signals = $signals;
        this.$timeout = $timeout;
        this.$bridges = $bridges;
    }

    /**
     * Link function to initialize the directive.
     * @param {Object} scope - Angular scope object.
     * @param {jQuery} element - jQuery object representing the textarea element.
     * @param {Object} attrs - Attributes of the textarea element.
     */
    link(scope, element, attrs) {
        let id = attrs.id;
        let placeholder = attrs.placeholder;
        let cssClass = attrs.class;

        element.attr('id', id);
        element.context.id = id;

        let currentTextarea = element[0];

        let offset = (currentTextarea.offsetHeight - currentTextarea.clientHeight);

        /**
         * Resize function to adjust the height of the textarea based on its content.
         * @param {HTMLElement} elem - The textarea element to resize.
         */
        let resize = (elem) => {
            elem.style.height = 'auto';
            elem.style.height = (elem.scrollHeight + offset) + 'px';
        };

        if (currentTextarea.addEventListener) {
            currentTextarea.addEventListener('input', () => {
                resize(currentTextarea);
            });
        }

        this.$signals.on("data-receive-" + id, () => {
            resize(currentTextarea);
        });

        if (currentTextarea.attachEvent) {
            currentTextarea.attachEvent('onkeyup', () => {
                resize(currentTextarea);
            });
        }

        scope.init = () => {
            scope.asyncFillTextarea();
        };

        scope.asyncFillTextarea = () => {
            this.$signals.emit(IB.Signals.SHEET_WAIT_REQUEST, element.attr('id'), "expandable-textarea");

            setTimeout(() => {
                resize(currentTextarea);
                this.$signals.emit(IB.Signals.SHEET_WAIT_REQUEST_CLOSED, element.attr('id'), "expandable-textarea");
            }, 1);
        };

        scope.onFocus = () => {
            this.$bridges.runtime().setLastKeyboardLanguage();
        };

        scope.init();
    }
}

ExpandableTextarea.$inject = ['$signals', '$timeout', '$bridges'];

function registerExpandableTextareaDirective(module) {
    module.directive('expandableTextarea', function() {
        return {
            restrict: 'E',
            replace: true,
            template: '<textarea ng-focus="onFocus()" rows="1" class="expandable-textarea" autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false"></textarea>',
            scope: true,
            controller: ExpandableTextarea
        };
    });
}

export default registerExpandableTextareaDirective;
